import {useState, useCallback, useRef, useEffect} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {tripService} from '@/services/tripService'
import {setCancelGate, useTripDetails} from '@/hooks/useTrips'
import CancelRideModal from '@/customComponents/uiComponents/global/CancelRideModal.jsx'
import {tripCancelPolicy} from '@/utils/functions'

/**
 * Global cancel-ride control — the SINGLE source for cancelling a trip. It
 * renders the trigger BUTTON and the cancellation MODAL together, owning its
 * own open-state and cancel mutation. Drop it in anywhere a trip can be
 * cancelled; multiple instances on one page each keep their own state
 * independently (e.g. one per row in a trips list).
 *
 *   <CancelRide trip={trip} className="…">Cancel</CancelRide>
 *
 * - `children`      → the button content (text, or icon + text).
 * - `className`     → the button styling (varies per page).
 * - `disabled` + `blockedReason` → gate the button with a hover tooltip.
 * - `tooltipAlign`  → 'right' (default), 'center', or 'full' — 'full' spans the nearest
 *   `relative` ancestor (e.g. a card's action row), for narrow cards where a button-anchored
 *   tooltip ran off the screen edge.
 * - `onSuccess`     → called after a genuine (non-stale) successful cancel.
 *
 * On success the popup shows its own "Ride Cancelled" screen and the user
 * leaves from there — this component never navigates.
 */
export default function CancelRide({
    trip,
    tripId = trip?.id,
    children = 'Cancel',
    className = '',
    disabled = false,
    blockedReason,
    tooltipAlign = 'right',
    onSuccess,
}) {
    const [open, setOpen] = useState(false)
    // True from the moment a cancel is submitted until the modal closes — drives the
    // cancel gate (pauses polling only then, so the success screen can't be unmounted).
    const [cancelling, setCancelling] = useState(false)
    const cancelledRef = useRef(false)
    const queryClient = useQueryClient()

    // The modal is entirely data-driven from `trip.cancellation_policy` (tiers, fee, refund
    // %, driver, charged card). Reuse the SAME query the trip-details page uses
    // (useTripDetails — same /trips/{id} endpoint, same ['trip', id] cache), so the modal is
    // consistent with that page and shows exactly what the API returns. A trips-LIST row's
    // cancellation_policy is a summary (tiers absent) and is NEVER trusted.
    //   - `enabled: open` → only the trip whose modal is open fetches (no poll per list row).
    //   - NOTE: the cancel gate (raised below) pauses trip polling WHILE a cancel modal is
    //     open, so a just-cancelled trip can't unmount the success screen — hence this
    //     REFETCHES FRESH on each open rather than polling continuously while open.
    const idKey = tripId != null ? String(tripId) : undefined
    const detailsQuery = useTripDetails(idKey, {live: true, enabled: open})
    const effectiveTrip = detailsQuery.data?.trip || trip
    // Loader while the shown trip has no tier list yet (a list row / placeholder) and a
    // fetch is in flight; the details page's full trip renders immediately.
    const shownHasTiers = Array.isArray(effectiveTrip?.cancellation_policy?.tiers)
    const loadingTrip = open && !shownHasTiers && detailsQuery.isFetching

    // Pause the trips polling ONLY while a cancel is in flight / its "Ride Cancelled"
    // success screen is showing — NOT for the whole time the modal is open. A poll would
    // otherwise drop the just-cancelled trip from its list and unmount this component,
    // killing the success screen. Keeping the gate DOWN while the user is merely deciding
    // lets the modal's own trip query keep live-polling (fresh driver/tiers); it goes UP
    // the instant a cancel is submitted (see handleCancel). The cleanup covers an
    // unexpected unmount too, so the gate never gets stuck.
    useEffect(() => {
        if (!cancelling) return
        setCancelGate(true)
        return () => setCancelGate(false)
    }, [cancelling])

    const cancelMutation = useMutation({
        mutationFn: (reason) => tripService.cancelTrip(tripId, reason),
    })

    // Returns truthy on success so the popup advances to (and stays on) its
    // "Ride Cancelled" screen. 409 = already cancelled → treat as stale success.
    // We DON'T refetch here — that happens on close (see handleClose).
    const handleCancel = useCallback(async (reason) => {
        // Gate polling as the cancel STARTS (before the trip can flip to Cancelled) and keep
        // it up for the success screen. Release it again only if the cancel didn't take, so
        // live polling resumes.
        setCancelling(true)
        try {
            const result = await cancelMutation.mutateAsync(reason)
            const stale = result?.statusCode === 409 || result?.raw?.code === 409
            const ok = !!result?.success || stale
            if (ok) {
                cancelledRef.current = true
                if (result?.success) onSuccess?.(result)
                // The cancellation-fee receipt link comes back in the cancel RESPONSE
                // (not on the trip until a later refetch). Return it so the modal's
                // "View Receipt" can open it; else return true (success, no fee receipt).
                const feeReceipt = result?.data?.fee_receipt_link || result?.data?.trip?.fee_receipt_link || null
                return feeReceipt || true
            }
            setCancelling(false)
            return false
        } catch {
            setCancelling(false)
            return false
        }
    }, [cancelMutation, onSuccess])

    // Refetch the trip / list only AFTER the user leaves the success screen.
    // Doing it on success would flip the trip to "cancelled" mid-flow, re-render
    // the host page and yank the success screen away — the auto-redirect we don't want.
    const handleClose = useCallback(() => {
        setOpen(false)
        setCancelling(false)   // release the gate → polling resumes for the (now refetched) trip
        if (cancelledRef.current) {
            cancelledRef.current = false
            queryClient.invalidateQueries({queryKey: ['trips']})
            queryClient.invalidateQueries({queryKey: ['trip', String(tripId)]})
        }
    }, [queryClient, tripId])

    // Cancellation gate, enforced HERE rather than at each call site: this component is the
    // only way to cancel, so gating it covers every entry point at once — including the
    // trips-list rows, which passed no `disabled` at all and so kept a live Cancel button on
    // a started ride. A call site's own `disabled`/`blockedReason` still applies on top
    // (the edit page has its own reasons); this can only ever ADD a block, never lift one.
    //
    // Read off `effectiveTrip` so the live poll closes the button the moment the ride
    // starts, instead of waiting for the host page to re-render.
    const gate = tripCancelPolicy(effectiveTrip)
    const isDisabled = disabled || !gate.canCancel
    const tooltipText = blockedReason || (gate.canCancel ? null : gate.blockedReason)

    const tooltipCls = tooltipAlign === 'full'
        ? "hidden group-hover:block absolute top-full left-0 right-0 mt-2 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10"
        : tooltipAlign === 'center'
        ? "hidden group-hover:block absolute top-full left-0 right-0 mx-auto mt-2 w-max max-w-[280px] bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-[12px] after:border-l-transparent after:border-r-[12px] after:border-r-transparent after:border-b-[16px] after:border-b-[#3c393c] after:-top-3.75 after:left-1/2 after:-translate-x-1/2"
        : "hidden group-hover:block absolute top-full right-0 mt-2 w-max max-w-[280px] bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-[12px] after:border-l-transparent after:border-r-[12px] after:border-r-transparent after:border-b-[16px] after:border-b-[#3c393c] after:-top-3.75 after:right-6"

    const button = (
        <button type="button" disabled={isDisabled} onClick={() => setOpen(true)} className={className}>
            {children}
        </button>
    )

    return (
        <>
            {tooltipText ? (
                <div className={`group ${tooltipAlign === 'full' ? '' : 'relative'}`}>
                    {button}
                    {isDisabled && (
                        <div className={tooltipCls}><p className="mb-0!">{tooltipText}</p></div>
                    )}
                </div>
            ) : button}

            <CancelRideModal
                isOpen={open}
                onClose={handleClose}
                onCancel={handleCancel}
                isCancelling={cancelMutation.isPending}
                trip={effectiveTrip}
                loadingTrip={loadingTrip}
            />
        </>
    )
}
